/** @file Solve-space length of a two-bone chain's child bone for a given elbow angle. */
import { trig } from '@pose2d/math/trig';
import type { Affine } from '@pose2d/math/types';

/**
 * Length of the child bone vector v = m·w, |w| = |childLen|, when v points along e = (ex, ey) turned by `psi`
 * radians (0 = straight chain). `m` is the parent's local linear part at rotation 0 and e the child joint in it.
 * Constant (|childLen| times the scale) for a similarity `m`; 0 when `m` is singular or e is zero.
 */
export function elbowSpan(m: Affine, ex: number, ey: number, childLen: number, psi: number): number {
  const a = m[0] ?? 0;
  const b = m[1] ?? 0;
  const c = m[2] ?? 0;
  const d = m[3] ?? 0;
  const det = a * d - b * c;
  const l1 = trig.hypot(ex, ey);
  if (det === 0 || l1 === 0) return 0;
  const cs = trig.cos(psi);
  const sn = trig.sin(psi);
  const ux = (ex * cs - ey * sn) / l1;
  const uy = (ex * sn + ey * cs) / l1;
  // v = |v|·u and w = m⁻¹·v, so |v| = |w| / |m⁻¹·u| = |childLen|·|det| / |adj(m)·u|.
  return (Math.abs(childLen) * Math.abs(det)) / trig.hypot(d * ux - b * uy, a * uy - c * ux);
}
